import { useGetSuperAdminFranchises } from '@agos/api-client/platform'
import { T, formatDay, tt } from '@agos/shared'
import { ExternalLink, Plus, Store } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link } from 'wouter'
import {
  DataTable,
  EmptyState,
  PageHeader,
  SearchInput,
  Spinner,
  StatusBadge,
  type Column,
} from '@agos/ui'

/**
 * Every franchise on the platform, and the way into each one - §3.2, FB-504.
 *
 * **This page is why `saas-franchises` exists.** It drew a card per tenant with the masked database
 * URL on it and **no link anywhere**, so `/franchises/:id` was unreachable - and with it the usage,
 * integrations and manager-settings pages it links to, and the provisioning wizard, which nothing
 * navigated to either. Six of the Tower's eighteen pages were behind a door with no handle.
 *
 * **The columns are legacy's** (`Franchises.tsx:70`): name, slug, primary domain, package, state and
 * created date, with a search over name, slug and domain. An operator identifies a tenant by its
 * **domain**, not by its connection string - which is the one field this page used to show and
 * legacy's does not.
 *
 * **The masked connection string is still here and still masked**, moved to its own column: it
 * arrives as `postgres://hellkitchen:***@postgres:5432/…` and is never put in an editable field,
 * because a form that round-trips `***` back into the registry turns a working connection string into
 * three asterisks.
 */

export interface FranchiseRow {
  id: number
  slug: string
  name: string
  isActive: boolean
  createdAt: string
  databaseUrl: string
  domains: { domain: string; isPrimary: boolean }[]
  packageName: string | null
}

function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

/** The rows as the endpoint answers them, ordered by name so the list does not move between loads. */
export function franchiseRows(payload: unknown): FranchiseRow[] {
  const body = (typeof payload === 'object' && payload !== null ? payload : {}) as {
    franchises?: unknown
  }
  const rows = Array.isArray(body.franchises) ? body.franchises : []
  return rows
    .map((entry) => {
      const row = (typeof entry === 'object' && entry !== null ? entry : {}) as Record<string, unknown>
      const domains = Array.isArray(row.domains) ? row.domains : []
      const bundle = (typeof row.package === 'object' && row.package !== null ? row.package : {}) as {
        name?: unknown
      }
      return {
        id: typeof row.id === 'number' ? row.id : 0,
        slug: text(row.slug),
        name: text(row.name),
        isActive: row.isActive === true,
        createdAt: text(row.createdAt),
        databaseUrl: text(row.databaseUrl),
        domains: domains.map((one) => {
          const domain = (typeof one === 'object' && one !== null ? one : {}) as Record<string, unknown>
          return { domain: text(domain.domain), isPrimary: domain.isPrimary === true }
        }),
        packageName: text(bundle.name).trim() === '' ? null : text(bundle.name).trim(),
      }
    })
    .sort((left, right) => left.name.localeCompare(right.name, 'sk'))
}

/**
 * The domain an operator would type to reach this tenant.
 *
 * **Falls back to the first domain rather than to nothing.** Measured: one of the three franchises has
 * an empty `domains` array and another has two, only one of them primary. A tenant with domains but
 * none flagged primary is a registry state nobody intended, and showing a dash for it would hide a
 * domain that works.
 */
export function primaryDomainOf(row: FranchiseRow): string | null {
  const flagged = row.domains.find((one) => one.isPrimary)?.domain
  if (flagged !== undefined && flagged.trim() !== '') return flagged
  const first = row.domains.find((one) => one.domain.trim() !== '')?.domain
  return first ?? null
}

/** Legacy searches name, slug **and** domain - the three things somebody remembers a tenant by. */
export function matchesFranchiseSearch(row: FranchiseRow, search: string): boolean {
  const needle = search.trim().toLowerCase()
  if (needle === '') return true
  return (
    row.name.toLowerCase().includes(needle) ||
    row.slug.toLowerCase().includes(needle) ||
    row.domains.some((one) => one.domain.toLowerCase().includes(needle))
  )
}

export function FranchisesPage() {
  const { data, isLoading, isError } = useGetSuperAdminFranchises()
  const [search, setSearch] = useState('')
  const rows = useMemo(() => franchiseRows(data), [data])
  const shown = rows.filter((row) => matchesFranchiseSearch(row, search))

  /*
   * The columns - §3.92.
   *
   * Sortable where sorting answers something an operator asks: by name to find one, by package to
   * see who is on which tier, by state to find the switched-off ones, by creation date to see the
   * newest. The domain and the database URL are not sortable - nobody orders tenants by connection
   * string, and a column header that sorts and shouldn't is noise on a seven-wide table.
   */
  const columns: Column<FranchiseRow>[] = [
    {
      key: 'name',
      header: tt('Meno'),
      sortable: true,
      sortValue: (row) => (row.name === '' ? row.slug : row.name).toLowerCase(),
      render: (row) => (
        /* The row's way in. Legacy makes the whole row clickable; a link is the same destination and
         * can be opened in a second tab, which an operator comparing two tenants actually does. */
        <Link href={`/franchises/${row.id}`} data-franchise-open={row.id} className="font-semibold underline">
          {row.name === '' ? row.slug : row.name}
        </Link>
      ),
    },
    {
      key: 'slug',
      header: 'Slug',
      className: 'font-mono text-xs text-muted-foreground',
      sortable: true,
      sortValue: (row) => row.slug,
      render: (row) => row.slug,
    },
    {
      key: 'domain',
      header: tt('Doména'),
      render: (row) => {
        const domain = primaryDomainOf(row)
        return (
          <span data-franchise-domain={row.slug}>
            {domain === null ? (
              <span className="text-muted-foreground">-</span>
            ) : (
              <span className="inline-flex items-center gap-1">
                {domain}
                <a
                  href={`https://${domain}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${tt('Otvoriť')} ${domain}`}
                  className="text-primary"
                >
                  <ExternalLink className="w-3 h-3" />
                </a>
              </span>
            )}
          </span>
        )
      },
    },
    {
      key: 'package',
      header: tt('Balíček'),
      sortable: true,
      sortValue: (row) => row.packageName ?? '',
      render: (row) => (
        <span data-franchise-package={row.slug}>
          {row.packageName ?? <span className="text-muted-foreground">-</span>}
        </span>
      ),
    },
    {
      key: 'state',
      header: tt('Stav'),
      sortable: true,
      // Inactive first when sorted ascending: a switched-off tenant is the one somebody is looking
      // for, and sorting a column of mostly-Aktívna the other way buries it.
      sortValue: (row) => (row.isActive ? 1 : 0),
      render: (row) => (
        /* §3.92 - `StatusBadge`, used three times in the whole Tower against fifty-two in the
         * manager. This was a hand-rolled span with its own palette; the shared one is the same
         * shape every state in the product wears. The hook is on a wrapper because the component
         * takes no `data-*`, and it has no e2e reference to lose. */
        <span data-franchise-active={row.isActive ? 'yes' : 'no'}>
          <StatusBadge
            label={row.isActive ? tt('Aktívna') : tt('Neaktívna')}
            color={row.isActive ? 'emerald' : 'zinc'}
          />
        </span>
      ),
    },
    {
      key: 'created',
      header: tt('Vytvorené'),
      className: 'text-xs text-muted-foreground',
      sortable: true,
      sortValue: (row) => row.createdAt,
      render: (row) => (
        <span data-franchise-created={row.slug}>{row.createdAt === '' ? '-' : formatDay(row.createdAt)}</span>
      ),
    },
    {
      key: 'database',
      header: tt('Databáza'),
      className: 'text-[11px] text-muted-foreground',
      render: (row) => <span data-franchise-db>{row.databaseUrl}</span>,
    },
  ]

  return (
    <main className="space-y-3" data-franchises>
      <PageHeader
        title={tt('Franšízy')}
        description={tt('Každá pobočka, jej balík a jej stav.')}
        icon={<Store className="w-5 h-5" />}
      />
      <div className="flex flex-wrap items-center gap-2">
        {/* §3.117 - the product's own search box, not a fourth reimplementation of one. `SearchInput`
          * is in 3 manager screens and was in **0** Tower ones, and it brings the thing the hand-rolled
          * version had no room for: a clear button. Searching a list of franchises and then wanting all
          * of them back meant selecting the text and deleting it. */}
        <SearchInput
          className="flex-1 min-w-[220px]"
          value={search}
          onChange={setSearch}
          placeholder={tt('Hľadať podľa mena, slugu alebo domény…')}
          label={tt('Hľadať franšízu')}
          testId="data-franchise-search"
        />
        {/* §3.2 - nothing navigated here. The provisioning wizard is §5's whole subject and it had no
          * entry point at all; legacy's list has this button (`Franchises.tsx:44`). */}
        <Link
          href="/franchises/new"
          /* `data-new-franchise-link`, not `data-new-franchise`: the wizard's own root already
            * carries that attribute, and one selector matching both the door and the room is a
            * selector that cannot tell you which one you are in. */
          data-new-franchise-link
          className="inline-flex items-center gap-1.5 min-h-[40px] px-3 rounded-xl bg-primary text-primary-foreground text-sm font-bold"
        >
          <Plus className="w-4 h-4" /> <T>Nová franšíza</T>
        </Link>
      </div>

      {isLoading && (
        <div className="py-10 flex justify-center" data-franchises-loading>
          <Spinner />
        </div>
      )}
      {/* §3.169, FB-920 - **the twenty-second error message with nothing announcing it.** FB-918 gave
        * `role="alert"` to twenty-one Tower banners by matching the bordered container, and this one is a
        * plain red sentence, so that sweep could not see it: `agos-a-ratchets-pattern-is-its-blind-spot`.
        * The attribute is additive and changes nothing on screen; leaving it as a `<p>` rather than
        * swapping in `<ReadFailed>` is deliberate, because that would restyle it, and a visual change is
        * not this entry's to make. `TODO.md` carries it. */}
      {isError && (
        <p className="text-xs text-red-500" data-franchises-error role="alert">
          <T>Franšízy sa nepodarilo načítať.</T>
        </p>
      )}

      {/* §3.169, FB-920 - **`&& !isError`, and this is the sharpest of the three.** A refused read makes
        * `rows` empty, so this drew *Žiadne franšízy* and *Prvú pobočku založíte tlačidlom Nová
        * franšíza* - **an instruction**, three lines under the banner saying the read failed, on the
        * screen that lists every tenant the platform has. FB-812's class, which outranks a wrong
        * sentence because somebody acts on it, and FB-826 already measured what acting on it costs:
        * *"creating a second one, whose slug collides with the first"*.
        *
        * The search half is untouched and stays correct: with a query typed and the read working,
        * *Nič nezodpovedá hľadaniu* is true. Only the branch that speaks for the platform is gated. */}
      {!isLoading && !isError && shown.length === 0 && (
        <div data-no-franchises>
          <EmptyState
            icon={<Store className="w-12 h-12" />}
            title={rows.length === 0 ? tt('Žiadne franšízy') : tt('Nič nezodpovedá hľadaniu')}
            description={
              rows.length === 0
                ? tt('Prvú pobočku založíte tlačidlom Nová franšíza.')
                : tt('Skúste iný názov alebo doménu.')
            }
          />
        </div>
      )}

      {/* §3.92 - `DataTable`, which the Tower used **zero** times while every one of its screens is
        * a list. Sorting is the point here: this is the page somebody opens to find a tenant, and
        * *which franchise is inactive* or *which was created last* were not questions it could
        * answer. Paging and a card layout for a phone arrive with it.
        *
        * `rowAttributes` keeps `data-franchise` on the row - the component carries that prop for
        * exactly this migration. **It draws each row twice**, a `<tr>` and a card, so the seven e2e
        * lines that locate a row are scoped to the table
        * (`agos-datatable-renders-every-row-twice`). */}
      {shown.length > 0 && (
        <DataTable<FranchiseRow>
          columns={columns}
          data={shown}
          rowKey={(row) => row.slug}
          rowAttributes={(row) => ({ 'data-franchise': row.slug })}
          emptyMessage={tt('Žiadne franšízy')}
        />
      )}
    </main>
  )
}
